/**
 * Top-level stat cards for the Clients page: active count, MRR, attention needed.
 */
import type { Client } from '../../lib/api'

interface ClientStatRowProps {
  clients: Client[]
  /** When false, the MRR card is hidden entirely. */
  showFinancials?: boolean
}

export function ClientStatRow({ clients, showFinancials = true }: ClientStatRowProps) {
  // Internal clients are excluded from active count and MRR totals (LXP-47)
  const billableClients = clients.filter((c) => c.kind !== 'internal')
  const active = billableClients.length
  const mrr = billableClients.reduce((sum, c) => sum + (c.mrr ?? 0), 0)
  const attention = billableClients.filter((c) => c.health !== 'good').length

  return (
    <div className="client-stat-row">
      <StatCard label="Active clients" value={String(active)} tag="live" tone="success" />
      {showFinancials && (
        <StatCard
          label="Monthly recurring"
          value={`$${mrr.toLocaleString()}`}
          tag="MRR"
          tone="accent"
        />
      )}
      <StatCard
        label="Needs attention"
        value={String(attention)}
        tag={attention ? 'review' : 'clear'}
        tone={attention ? 'warning' : 'success'}
      />
    </div>
  )
}

type Tone = 'accent' | 'success' | 'warning' | 'danger' | 'neutral'

const TONE_STYLES: Record<Tone, { color: string; bg: string; border: string }> = {
  accent: {
    color: 'var(--accent-primary)',
    bg: 'var(--accent-soft)',
    border: 'var(--accent-edge)',
  },
  success: {
    color: 'var(--state-success)',
    bg: 'var(--success-soft)',
    border: 'rgba(63,182,139,0.35)',
  },
  warning: {
    color: 'var(--state-warning)',
    bg: 'var(--warning-soft)',
    border: 'rgba(224,169,59,0.35)',
  },
  danger: {
    color: 'var(--state-danger)',
    bg: 'var(--danger-soft)',
    border: 'rgba(229,72,77,0.35)',
  },
  neutral: {
    color: 'var(--text-secondary)',
    bg: 'var(--bg-elevated)',
    border: 'var(--border-default)',
  },
}

function StatCard({
  label,
  value,
  tag,
  tone,
}: {
  label: string
  value: string
  tag: string
  tone: Tone
}) {
  const t = TONE_STYLES[tone]
  return (
    <div className="client-stat-card">
      <div className="client-stat-card__header">
        <span className="client-stat-card__label">{label}</span>
        <span
          className="client-stat-card__tag"
          style={{ color: t.color, background: t.bg, border: `1px solid ${t.border}` }}
        >
          {tag}
        </span>
      </div>
      <div className="client-stat-card__value mono">{value}</div>
    </div>
  )
}
